// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.
import * as yup from 'yup'
import { DefiCollateralCTO, DefiCollateralSchema, DefiType } from './CollateralSetCTO'
import { ERC20Wei } from './conversions'
import { EXCHANGE_RATE_ONE, yupErc20WeiRequired } from './Enums'
import { ReportingColumnVisibilityEnum, StringTypeEnum, toFieldMetaData } from './SchemaTypes'
import { FeeAccrualCTO, FeeAccrualCTOSchema } from './FeeAccrualCTO'

export enum ExchangeRateChangeableStatus {
  Undefined = 'Undefined',
  NoMaterialChange = 'NoMaterialChange',
  ClearToChange = 'ClearToChange',
  DiscrepancyDetectedOnHold = 'DiscrepancyDetectedOnHold',
  ExcessiveChangeOnHold = 'ExcessiveChangeOnHold',
}
const DefiDataSchemaValues = {
  defiType: yup
    .string<DefiType>()
    .oneOf(Object.values(DefiType))
    .required()
    .default(DefiType.Undefined)
    .label('Defi Type')
    .meta(
      toFieldMetaData({
        propName: 'defiType',
        description: 'Defi type. ',

        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 1,
      })
    ),
  interestRate: yup
    .number()
    .required()
    .label('Interest Rate')
    .meta(
      toFieldMetaData({
        propName: 'interestRate',
        description: 'Interest rate. ',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 2,
      })
    ),

  defiTotalShares: yupErc20WeiRequired.label('Defi Total Shares').meta(
    toFieldMetaData({
      propName: 'defiTotalShares',
      description: 'Defi total shares. ',
      stringType: StringTypeEnum.PPT_SHARES,
    })
  ),
  defiTotalAssets: yupErc20WeiRequired
    .required()
    .label('Defi Total Assets')
    .meta(
      toFieldMetaData({
        propName: 'defiTotalAssets',
        description: 'Defi total assets. ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  defiOTShares: yupErc20WeiRequired.label('Defi OT Shares').meta(
    toFieldMetaData({
      propName: 'defiOTShares',
      description: 'Defi Open Trade shares. ',
      stringType: StringTypeEnum.PPT_SHARES,
    })
  ),
  defiOTAssets: yupErc20WeiRequired
    .required()
    .label('Defi OT Assets')
    .default('0' as ERC20Wei)
    .meta(
      toFieldMetaData({
        propName: 'defiOTAssets',
        description: 'Defi Open Trade assets. ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

  calibratedExchangeRateBeforeFees: yupErc20WeiRequired
    .required()
    .label('Calibrated Exchange Rate Before Fees')
    .meta(
      toFieldMetaData({
        propName: 'calibratedExchangeRateBeforeFees',
        description: 'Calibrated exchange rate. ',
        stringType: StringTypeEnum.EXCHANGE_RATE_LONG,
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 3,
      })
    ),
  calibratedBalance: yupErc20WeiRequired
    .required()
    .label('Calibrated Balance')
    .meta(
      toFieldMetaData({
        propName: 'calibratedBalance',
        description: 'Calibrated balance. ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

  decimals: yup
    .number()
    .required()
    .label('Defi Vault Decimals')
    .meta(
      toFieldMetaData({
        propName: 'decimals',
        description: 'Decimals in Defi Vault',
      })
    ),
  nativeDefiExchangeRateBeforeFees: yupErc20WeiRequired
    .required()
    .label('Native Defi Exchange Rate Before Fees')
    .meta(
      toFieldMetaData({
        propName: 'nativeDefiExchangeRateBeforeFees',
        description: 'Native defi exchange rate before fees. ',
        stringType: StringTypeEnum.EXCHANGE_RATE_LONG,
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 4,
      })
    ),

  collateralCTO: DefiCollateralSchema.required()
    .label('Collateral CTO')
    .meta(toFieldMetaData({ propName: 'collateralCTO', description: 'Collateral CTO. ' })),
}
export const DefiDataSchema = yup.object<DefiData>().shape(DefiDataSchemaValues)
export const DefiExchangeRateCTOSchema = yup.object<DefiExchangeRateCTO>().shape({
  ...DefiDataSchemaValues,

  otShares: yupErc20WeiRequired.label('OT Shares').meta(
    toFieldMetaData({
      propName: 'otShares',
      description: 'OT shares. ',
      stringType: StringTypeEnum.PPT_SHARES,
    })
  ),
  otAssets: yupErc20WeiRequired
    .required()
    .label('OT Assets')
    .meta(
      toFieldMetaData({
        propName: 'otAssets',
        description: 'OT assets. ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  totalPoolAssets: yupErc20WeiRequired
    .required()
    .label('Total OT Pool Assets')
    .meta(
      toFieldMetaData({
        propName: 'totalPoolAssets',
        description: 'Total OT pool assets. ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

  totalPoolShares: yupErc20WeiRequired
    .required()
    .label('Total Pool Shares')
    .meta(
      toFieldMetaData({
        propName: 'totalPoolShares',
        description: 'Total pool shares. ',
        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),

  currentExchangeRate: yupErc20WeiRequired
    .required()
    .default(EXCHANGE_RATE_ONE.toString() as ERC20Wei)
    .label('Current Exchange Rate')
    .meta(toFieldMetaData({ propName: 'currentExchangeRate', stringType: StringTypeEnum.EXCHANGE_RATE_LONG })),

  assetBasedExchangeRate: yupErc20WeiRequired
    .required()
    .default(EXCHANGE_RATE_ONE.toString() as ERC20Wei)
    .label('Expected Exchange Rate by Assets Calculation')
    .meta(
      toFieldMetaData({
        propName: 'assetBasedExchangeRate',
        stringType: StringTypeEnum.EXCHANGE_RATE_LONG,
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 5,
      })
    ),

  assetDelta: yupErc20WeiRequired
    .required()
    .label('Asset Delta')
    .meta(
      toFieldMetaData({
        propName: 'assetDelta',
        description: 'Asset delta. ',
        stringType: StringTypeEnum.STABLE_COINS,
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 6,
      })
    ),

  isNativeDefiReconciled: yup
    .boolean()
    .required()
    .label('Is Native Defi Reconciled')
    .meta(
      toFieldMetaData({
        propName: 'isNativeDefiReconciled',
        description: 'Is native defi reconciled. ',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 8,
      })
    ),
  isAssetsReconciled: yup
    .boolean()
    .required()
    .label('Is Assets Reconciled')
    .meta(
      toFieldMetaData({
        propName: 'isAssetsReconciled',
        description: 'Is assets reconciled. ',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 9,
      })
    ),
  exchangeRateChangeableStatus: yup
    .string<ExchangeRateChangeableStatus>()
    .oneOf(Object.values(ExchangeRateChangeableStatus))
    .required()
    .label('Set Exchange Rate Changeability Status')
    .meta(
      toFieldMetaData({
        propName: 'exchangeRateChangeableStatus',
        description: 'Set exchange rate change status. ',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 10,
      })
    ),

  calibratedExchangeRate: yupErc20WeiRequired
    .required()
    .label('Calibrated Exchange Rate')
    .meta(
      toFieldMetaData({
        propName: 'calibratedExchangeRate',
        description: 'Calibrated exchange rate after fees. ',
        stringType: StringTypeEnum.EXCHANGE_RATE_LONG,
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 11,
      })
    ),
  nativeDefiExchangeRate: yupErc20WeiRequired
    .required()
    .label('Native Defi Exchange Rate')
    .meta(
      toFieldMetaData({
        propName: 'nativeDefiExchangeRate',
        description: 'Native defi exchange rate after fees. ',
        stringType: StringTypeEnum.EXCHANGE_RATE_LONG,
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 12,
      })
    ),
  exchangeRateTimestamp: yup
    .number()
    .required()
    .label('Timestamp')
    .meta(
      toFieldMetaData({
        propName: 'exchangeRateTimestamp',
        description: 'Timestamp. ',
        stringType: StringTypeEnum.ETH_DATE,
      })
    ),
  exchangeRateTimestampDateString: yup
    .string()
    .required()
    .label('Timestamp Date')
    .default(' ')
    .meta(
      toFieldMetaData({
        propName: 'exchangeRateTimestampDateString',
        description: 'Timestamp Date of the data',
        stringType: StringTypeEnum.ETH_DATE_TIME_STRING,
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 13,
      })
    ),
  feeAccrualCTO: FeeAccrualCTOSchema.required()
    .label('Fee Accrual CTO')
    .meta(toFieldMetaData({ propName: 'feeAccrualCTO', description: 'Fee accrual CTO. ' })),
})

export interface DefiData {
  defiType: DefiType
  interestRate: number

  defiTotalShares: ERC20Wei
  defiTotalAssets: ERC20Wei
  defiOTShares: ERC20Wei
  defiOTAssets: ERC20Wei

  decimals: number
  calibratedBalance: ERC20Wei
  calibratedExchangeRateBeforeFees: ERC20Wei
  nativeDefiExchangeRateBeforeFees: ERC20Wei

  collateralCTO: DefiCollateralCTO
}
export interface DefiExchangeRateCTO extends DefiData {
  exchangeRateTimestamp: number
  exchangeRateTimestampDateString: string
  feeAccrualCTO: FeeAccrualCTO
  exchangeRateChangeableStatus: ExchangeRateChangeableStatus
  otShares: ERC20Wei
  otAssets: ERC20Wei

  totalPoolAssets: ERC20Wei
  totalPoolShares: ERC20Wei
  assetDelta: ERC20Wei

  assetBasedExchangeRate: ERC20Wei
  calibratedExchangeRate: ERC20Wei
  nativeDefiExchangeRate: ERC20Wei

  currentExchangeRate: ERC20Wei

  isNativeDefiReconciled: boolean
  isAssetsReconciled: boolean
}

type DefiDataInfer = yup.InferType<typeof DefiDataSchema>
export const _testADefiData: DefiData = {} as unknown as DefiDataInfer
export const _testBDefiData: DefiDataInfer = {} as unknown as DefiData

type DefiExchangeRateCTOInfer = yup.InferType<typeof DefiExchangeRateCTOSchema>
export const _testAPoolDynamicAccount: DefiExchangeRateCTO = {} as unknown as DefiExchangeRateCTOInfer
export const _testBPoolDynamicAccount: DefiExchangeRateCTOInfer = {} as unknown as DefiExchangeRateCTO
